import React, { useState, useMemo, useRef } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { MdBusinessCenter, MdEmail, MdPhone, MdLocationOn } from "react-icons/md";
import "./ModalForm.css";
import SEO from "../../components/SEO";

// Icons Components
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const SpinIcon = () => (
  <svg className="ar-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

// Constants
const INITIAL_FORM_STATE = {
  name: "",
  companyName: "",
  email: "",
  workEmail: "",
  phone: "",
  country: "",
  businessCategory: "",
  message: "",
};

const BUSINESS_CATEGORIES = [
  { value: "startup", label: "Startup" },
  { value: "msme", label: "MSME" },
  { value: "mid to large", label: "Mid to Large" },
  { value: "large enterprise", label: "Large Enterprise" },
];

const DEFAULT_COUNTRY = {
  value: "IN",
  label: "India",
};

// Toast configuration with longer durations
const TOAST_CONFIG = {
  success: {
    autoClose: 8000, // 8 seconds for success messages
    pauseOnHover: true,
    pauseOnFocusLoss: true,
    closeOnClick: true,
    draggable: true,
    position: "top-right",
  },
  error: {
    autoClose: 7000, // 7 seconds for error messages
    pauseOnHover: true,
    pauseOnFocusLoss: true,
    closeOnClick: true,
    draggable: true,
    position: "top-right",
  },
  info: {
    autoClose: 5000, // 5 seconds for info messages
    pauseOnHover: true,
    pauseOnFocusLoss: true,
    closeOnClick: true,
    draggable: true,
    position: "top-right",
  }
};

export default function ModalForm() {
  // State Management
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // Refs to track toast ID and timeout
  const toastIdRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  const navigate = useNavigate();

  // Toast management functions
  const showToast = (message, type = "error", customConfig = {}) => {
    // Dismiss existing toast if any
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }

    // Clear any pending reset timeout
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    // Merge default config with custom config
    const config = {
      ...TOAST_CONFIG[type] || TOAST_CONFIG.info,
      ...customConfig,
      onClose: () => {
        toastIdRef.current = null;
        if (customConfig.onClose) {
          customConfig.onClose();
        }
      }
    };

    // Show new toast and store its ID
    const toastId = toast[type](message, config);
    toastIdRef.current = toastId;
    return toastId;
  };

  // Memoized Country Options
  const countryOptions = useMemo(() => {
    return countryList()
      .getData()
      .flatMap((country) => {
        try {
          return [{
            ...country,
            dialCode: "+" + getCountryCallingCode(country.value),
          }];
        } catch {
          return [];
        }
      });
  }, []);

  // Country-specific phone validation
  const validatePhoneForCountry = (phoneNumber, countryCode) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Country-specific validation
    switch (countryCode) {
      case 'IN': // India
        // Must be exactly 10 digits and start with 6,7,8,9
        if (cleaned.length !== 10) {
          return { valid: false, message: "Indian phone number must be exactly 10 digits" };
        }
        if (!/^[6-9]/.test(cleaned)) {
          return { valid: false, message: "Indian phone number must start with 6, 7, 8, or 9" };
        }
        if (!/^\d{10}$/.test(cleaned)) {
          return { valid: false, message: "Indian phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'US': // United States
        if (cleaned.length !== 10) {
          return { valid: false, message: "US phone number must be exactly 10 digits" };
        }
        if (!/^\d{10}$/.test(cleaned)) {
          return { valid: false, message: "US phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'GB': // United Kingdom
        if (cleaned.length < 10 || cleaned.length > 11) {
          return { valid: false, message: "UK phone number must be 10-11 digits" };
        }
        if (!/^\d{10,11}$/.test(cleaned)) {
          return { valid: false, message: "UK phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'CA': // Canada
        if (cleaned.length !== 10) {
          return { valid: false, message: "Canadian phone number must be exactly 10 digits" };
        }
        if (!/^\d{10}$/.test(cleaned)) {
          return { valid: false, message: "Canadian phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'AU': // Australia
        if (cleaned.length < 9 || cleaned.length > 10) {
          return { valid: false, message: "Australian phone number must be 9-10 digits" };
        }
        if (!/^\d{9,10}$/.test(cleaned)) {
          return { valid: false, message: "Australian phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'DE': // Germany
        if (cleaned.length < 10 || cleaned.length > 11) {
          return { valid: false, message: "German phone number must be 10-11 digits" };
        }
        if (!/^\d{10,11}$/.test(cleaned)) {
          return { valid: false, message: "German phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'FR': // France
        if (cleaned.length !== 9) {
          return { valid: false, message: "French phone number must be exactly 9 digits" };
        }
        if (!/^\d{9}$/.test(cleaned)) {
          return { valid: false, message: "French phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      case 'JP': // Japan
        if (cleaned.length < 10 || cleaned.length > 11) {
          return { valid: false, message: "Japanese phone number must be 10-11 digits" };
        }
        if (!/^\d{10,11}$/.test(cleaned)) {
          return { valid: false, message: "Japanese phone number must contain only digits" };
        }
        return { valid: true, cleaned };

      default:
        // For other countries, use libphonenumber-js for validation
        try {
          const fullPhoneNumber = `+${getCountryCallingCode(countryCode)}${cleaned}`;
          const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber, countryCode);

          if (!phoneNumber || !phoneNumber.isValid()) {
            return {
              valid: false,
              message: `Please enter a valid phone number for ${selectedCountry.label}`
            };
          }

          if (!phoneNumber.isPossible()) {
            return {
              valid: false,
              message: `Phone number is not possible for ${selectedCountry.label}`
            };
          }

          return { valid: true, cleaned };
        } catch (err) {
          return {
            valid: false,
            message: "Please enter a valid phone number"
          };
        }
    }
  };

  // Validation Functions
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value || value.trim().length < 2) {
          error = "Full name is required (minimum 2 characters)";
        } else if (!/^[a-zA-Z\s\-']+$/.test(value)) {
          error = "Name should only contain letters, spaces, hyphens, and apostrophes";
        }
        break;

      case "companyName":
        if (!value || value.trim().length < 2) {
          error = "Company name is required (minimum 2 characters)";
        }
        break;

      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "businessCategory":
        if (!value) {
          error = "Please select a business category";
        }
        break;

      case "phone":
        if (!value) {
          error = "Phone number is required";
        } else {
          // Remove spaces and special characters for validation
          const cleanedPhone = value.replace(/[\s\-()]/g, '');

          if (!/^\d+$/.test(cleanedPhone)) {
            error = "Phone number should only contain digits";
          } else {
            // Validate with country-specific rules
            const validation = validatePhoneForCountry(cleanedPhone, selectedCountry.value);
            if (!validation.valid) {
              error = validation.message;
            }
          }
        }
        break;

      case "message":
        if (!value || value.trim().length < 10) {
          error = "Message is required (minimum 10 characters)";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = ["name", "companyName", "email", "businessCategory", "phone", "message"];

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, formData[fieldName]);
    if (error) {
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    // For phone input, only allow digits, spaces, hyphens, and parentheses
    if (name === "phone") {
      // Allow user to type spaces, hyphens, and parentheses for readability
      // but we'll clean it before validation and submission
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const cleanPhoneNumber = (phone) => {
    // Remove all spaces, hyphens, parentheses, and other non-digit characters
    return phone.replace(/\D/g, '');
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setSelectedCountry(DEFAULT_COUNTRY);
    setErrors({});
    setTouched({});
    setIsButtonDisabled(false);
    setSubmitting(false);
    // Clear any existing toast
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  };

  const delayedReset = (delay = 1000) => {
    // Clear any existing reset timeout
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    // Set new timeout for reset
    resetTimeoutRef.current = setTimeout(() => {
      resetForm();
      resetTimeoutRef.current = null;
    }, delay);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isButtonDisabled || submitting) {
      return;
    }

    // Immediately disable the button
    setIsButtonDisabled(true);
    setSubmitting(true);

    // Mark all fields as touched
    const allFields = ["name", "companyName", "email", "businessCategory", "phone", "message"];
    const touchedAll = {};
    allFields.forEach(field => {
      touchedAll[field] = true;
    });
    setTouched(touchedAll);

    // Clean phone number before validation
    const cleanedPhone = cleanPhoneNumber(formData.phone);

    // Update form data with cleaned phone number
    const cleanedFormData = {
      ...formData,
      phone: cleanedPhone
    };
    setFormData(cleanedFormData);

    // Validate all fields with cleaned data
    const newErrors = {};
    const fieldsToValidate = ["name", "companyName", "email", "businessCategory", "phone", "message"];

    fieldsToValidate.forEach(field => {
      const value = field === "phone" ? cleanedPhone : formData[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Show first error in toast with error configuration
      const firstError = Object.values(newErrors)[0];
      if (firstError) {
        showToast(firstError, "error");
      } else {
        showToast("Please fill all required fields correctly", "error");
      }
      // Re-enable button if validation fails
      setIsButtonDisabled(false);
      setSubmitting(false);
      return;
    }

    try {
      const fullPhoneNumber = `+${getCountryCallingCode(selectedCountry.value)}${cleanedPhone}`;

      const response = await fetch(`${import.meta.env.VITE_API_URL}/submit-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          country: selectedCountry.label,
          phone: fullPhoneNumber, // Send cleaned and formatted phone number
          submittedAt: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Show success toast with longer duration
        showToast(data.message || "Form submitted successfully!", "success", {
          autoClose: 3000, // 10 seconds for success message
          pauseOnHover: true,
          pauseOnFocusLoss: true,
          closeOnClick: true,
          draggable: true,
          // Don't reset immediately - wait for toast to be dismissed or timeout
          onClose: () => {
            // Reset form after toast is closed/dismissed
            resetForm();
            toastIdRef.current = null;
          }
        });

        // Re-enable button but don't reset form immediately
        setIsButtonDisabled(false);
        setSubmitting(false);

        // Backup reset in case toast doesn't close properly (after 12 seconds)
        delayedReset(12000);

      } else {
        // Show error toast for server error
        showToast("Save failed. Please try again.", "error", {
          autoClose: 5000,
          pauseOnHover: true,
          pauseOnFocusLoss: true,
          closeOnClick: true,
          draggable: true,
        });
        // Re-enable button on error
        setIsButtonDisabled(false);
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Form submission error:", error);

      // Show network error toast with longer duration
      showToast("Network error. Please check your connection and try again.", "error", {
        autoClose: 5000,
        pauseOnHover: true,
        pauseOnFocusLoss: true,
        closeOnClick: true,
        draggable: true,
      });

      // Re-enable button on error
      setIsButtonDisabled(false);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clear any pending timeouts
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
    navigate(-1);
  };

  // Render Components
  const renderFormRow = (leftChild, rightChild) => (
    <div className="cf_form_row">
      {leftChild}
      {rightChild}
    </div>
  );

  const renderInputWithIcon = (Icon, name, placeholder, type = "text", required = true) => {
    const hasError = errors[name] && touched[name];

    return (
      <div className="cf_form_group">
        <div className={`cf_input_icon_wrapper ${hasError ? 'cf_error' : ''}`}>
          <Icon size={16} className="cf_input_icon" />
          <input
            type={type}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={() => handleFieldBlur(name)}
            required={required}
            placeholder={placeholder + "*"}
            className={`cf_input_field ${hasError ? 'cf_input_error' : ''}`}
          />
        </div>
        {hasError && <span className="cf_error_message">{errors[name]}</span>}
      </div>
    );
  };

  const renderPhoneInput = () => {
    const hasError = errors.phone && touched.phone;

    return (
      <div className="cf_form_group">
        <div className={`cf_phone_container ${hasError ? 'cf_error' : ''}`}>
          <div className="cf_country_select_wrapper">
            <Select
              options={countryOptions}
              value={selectedCountry}
              isSearchable
              onChange={setSelectedCountry}
              className="cf_country_select"
              classNamePrefix="react-select"
            />
          </div>
          <div className={`cf_input_icon_wrapper cf_phone_input_wrapper ${hasError ? 'cf_error' : ''}`}>
            <MdPhone size={16} className="cf_input_icon" />
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("phone")}
              placeholder="Phone number * (e.g., 123-456-7890)"
              className={`cf_input_field cf_phone_input ${hasError ? 'cf_input_error' : ''}`}
              required
            />
          </div>
        </div>
        {hasError && <span className="cf_error_message">{errors.phone}</span>}
      </div>
    );
  };

  const renderBusinessCategorySelect = () => {
    const hasError = errors.businessCategory && touched.businessCategory;

    return (
      <div className="cf_form_group">
        <div className={`cf_input_icon_wrapper ${hasError ? 'cf_error' : ''}`}>
          <MdBusinessCenter size={16} className="cf_input_icon" />
          <select
            name="businessCategory"
            value={formData.businessCategory}
            onChange={handleChange}
            onBlur={() => handleFieldBlur("businessCategory")}
            required
            className={`cf_input_field cf_input_select ${hasError ? 'cf_input_error' : ''}`}
          >
            <option className="cf_select_option" value="">
              Select business category <span>*</span>
            </option>
            {BUSINESS_CATEGORIES.map((category) => (
              <option key={category.value} className="cf_select_option" value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
        {hasError && <span className="cf_error_message">{errors.businessCategory}</span>}
      </div>
    );
  };

  const renderMessageTextarea = () => {
    const hasError = errors.message && touched.message;

    return (
      <div className="cf_form_group">
        <div className={`cf_textarea_wrapper ${hasError ? 'cf_error' : ''}`}>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={() => handleFieldBlur("message")}
            required
            rows={4}
            placeholder="Tell us about your goals, challenges, and what you'd like to discuss with BAIREES."
            className={`cf_textarea_field ${hasError ? 'cf_input_error' : ''}`}
          />
          <span className="cf_required_indicator cf_textarea_indicator">*</span>
        </div>
        {hasError && <span className="cf_error_message">{errors.message}</span>}
      </div>
    );
  };

  const renderFormActions = () => (
    <div className="cf_form_actions">
      <button
        type="button"
        className="cf_button cf_button_cancel"
        onClick={handleCancel}
        disabled={isButtonDisabled}
      >
        Back
      </button>
      <button
        className="cf_button cf_button_submit"
        onClick={handleSubmit}
        disabled={isButtonDisabled || submitting}
        type="submit"
      >
        {submitting ? (
          <>
            <SpinIcon /> Submitting...
          </>
        ) : (
          <>
            Submit <ArrowRight />
          </>
        )}
      </button>
    </div>
  );

  return (
    <>
      <SEO
        title="Book a Consultation | BAIREES Global Advisory"
        description="Book a consultation with BAIREES Global Advisory — start the conversation on AI strategy and digital transformation for your organisation."
      />
      <div className="cf_wrapper">
        <div className="cf_container">
          <h2 className="cf_title">
            Tell us about <span className="cf_gold_gradient">you</span>
          </h2>

          <form className="cf_form" onSubmit={handleSubmit} noValidate>
            {/* Row 1: Name + Company */}
            {renderFormRow(
              renderInputWithIcon(FaUser, "name", "Full name"),
              renderInputWithIcon(MdBusinessCenter, "companyName", "Company name")
            )}

            {/* Row 2: Email + Business Category */}
            {renderFormRow(
              renderInputWithIcon(MdEmail, "email", "Email", "email"),
              renderBusinessCategorySelect()
            )}

            {/* Row 3: Phone Input (Full width) */}
            <div className="cf_form_row cf_phone_row">
              {renderPhoneInput()}
            </div>

            {/* Message */}
            {renderMessageTextarea()}

            {/* Buttons */}
            {renderFormActions()}
          </form>
        </div>
      </div>
    </>
  );
}